import React, { useEffect, useRef, useState } from "react";
import { fabric } from "fabric";
import { Layers } from "lucide-react";
import { ensureTextFontsLoaded, loadTextFont, primaryFontFamily } from "../../../designeditor/utils/textFonts";

const PREVIEW_WIDTH = 480;
/** Rendered previews survive re-renders and page changes. Key: content id + layout JSON length. */
const previewCache = new Map();

function parseCanva(canva) {
  try {
    if (!canva) return null;
    if (typeof canva === "string") return JSON.parse(canva);
    if (typeof canva.canva === "string") return JSON.parse(canva.canva);
    if (typeof canva === "object") return canva.canva || canva;
  } catch {
    return null;
  }
  return null;
}

async function renderPreview(data) {
  const w = data.width || data.canvasWidth || 1920;
  const h = data.height || data.canvasHeight || 1080;

  // Texts are measured at load time, so their web fonts must be ready first.
  ensureTextFontsLoaded();
  const families = [
    ...new Set((data.objects || []).filter((o) => o && o.fontFamily).map((o) => primaryFontFamily(o.fontFamily))),
  ];
  await Promise.all(families.map(loadTextFont));
  if (fabric.util && typeof fabric.util.clearFabricFontCache === "function") fabric.util.clearFabricFontCache();

  const el = document.createElement("canvas");
  const canvas = new fabric.StaticCanvas(el, {
    width: w,
    height: h,
    backgroundColor: data.backgroundColor || "#ffffff",
    renderOnAddRemove: false,
    enableRetinaScaling: false,
  });
  try {
    await new Promise((resolve) => canvas.loadFromJSON(data, resolve));
    canvas.renderAll();
    return canvas.toDataURL({ format: "jpeg", quality: 0.85, multiplier: PREVIEW_WIDTH / w });
  } finally {
    canvas.dispose();
  }
}

/**
 * Small rendered image of a layout ("template") for content cards. Draws only once the
 * card scrolls into view; falls back to an icon when the layout cannot be rendered
 * (e.g. an external image taints the canvas).
 */
export default function TemplatePreview({ id, canva, fill = true }) {
  const key = `${id}:${typeof canva === "string" ? canva.length : JSON.stringify(canva || "").length}`;
  const [src, setSrc] = useState(() => previewCache.get(key) || null);
  const [failed, setFailed] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (previewCache.has(key)) {
      setSrc(previewCache.get(key));
      return undefined;
    }
    const data = parseCanva(canva);
    if (!data) {
      setFailed(true);
      return undefined;
    }
    let cancelled = false;
    const start = () => {
      renderPreview(data)
        .then((url) => {
          previewCache.set(key, url);
          if (!cancelled) setSrc(url);
        })
        .catch(() => {
          if (!cancelled) setFailed(true);
        });
    };
    const box = boxRef.current;
    if (!box || typeof IntersectionObserver === "undefined") {
      start();
      return () => {
        cancelled = true;
      };
    }
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        io.disconnect();
        start();
      }
    });
    io.observe(box);
    return () => {
      cancelled = true;
      io.disconnect();
    };
  }, [key, canva]);

  return (
    <div
      ref={boxRef}
      style={{
        position: fill ? "absolute" : "relative",
        inset: fill ? 0 : undefined,
        height: fill ? "100%" : "230px",
        width: fill ? "100%" : "230px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f1f5f9",
      }}
    >
      {src ? (
        <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
      ) : (
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: 16,
            background: "rgba(0, 128, 128, 0.1)", // --primary-color-rgba is solid teal, not a tint
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: failed ? 1 : 0.6,
          }}
        >
          <Layers size={30} color="var(--primary-color)" />
        </div>
      )}
    </div>
  );
}
